import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const empty = {
  sampleDate: new Date().toISOString().split('T')[0],
  sampleSource: 'grain_lot',
  referenceNumber: '',
  referenceId: '',
  testMethod: '',
  resultPpb: '',
  actionTaken: '',
  instrumentId: '',
  instrumentCalibrationDate: '',
  testedBy: '',
  notes: '',
};

const AflatoxinForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (!isEdit) return;
    millingApi.getAflatoxinRecord(id!).then((r) => {
      const f: any = { ...empty };
      Object.keys(empty).forEach((k) => { f[k] = r[k] != null ? String(r[k]) : ''; });
      setForm(f);
    }).catch(() => setError('Failed to load record')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f: any) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload: any = { ...form };
    if (payload.resultPpb !== '') payload.resultPpb = Number(payload.resultPpb);
    else delete payload.resultPpb;
    if (!payload.referenceId) delete payload.referenceId;
    if (!payload.instrumentCalibrationDate) delete payload.instrumentCalibrationDate;
    try {
      if (isEdit) await millingApi.updateAflatoxinRecord(id!, payload);
      else await millingApi.createAflatoxinRecord(payload);
      navigate('/milling/aflatoxin');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const ppbVal = Number(form.resultPpb);
  const overLimit = form.resultPpb !== '' && ppbVal > 10;

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/aflatoxin')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Aflatoxin Record' : 'New Aflatoxin Test'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Sample Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Sample Date *" type="date" fullWidth size="small" value={form.sampleDate}
                  onChange={set('sampleDate')} InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sample Source *</InputLabel>
                  <Select value={form.sampleSource} label="Sample Source *"
                    onChange={(e) => setForm((f: any) => ({ ...f, sampleSource: e.target.value }))}>
                    <MenuItem value="grain_lot">Grain Lot</MenuItem>
                    <MenuItem value="silo">Silo</MenuItem>
                    <MenuItem value="in_process">In Process</MenuItem>
                    <MenuItem value="finished_product">Finished Product</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Reference Number" fullWidth size="small" value={form.referenceNumber}
                  onChange={set('referenceNumber')} helperText="Lot/silo/order number" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Reference ID (UUID)" fullWidth size="small" value={form.referenceId}
                  onChange={set('referenceId')} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Tested By" fullWidth size="small" value={form.testedBy}
                  onChange={set('testedBy')} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Test Method" fullWidth size="small" value={form.testMethod}
                  onChange={set('testMethod')} helperText="e.g. ELISA, HPLC, RapidTest" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Test Result</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Result (ppb)" type="number" fullWidth size="small"
                  value={form.resultPpb} onChange={set('resultPpb')}
                  inputProps={{ step: 0.1, min: 0 }}
                  error={overLimit}
                  helperText={overLimit ? '⚠ Exceeds 10 ppb limit — action required' : 'Limit: 10 ppb'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <TextField label="Action Taken" fullWidth size="small" value={form.actionTaken}
                  onChange={set('actionTaken')} helperText="If result > 10 ppb" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Instrument</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Instrument ID" fullWidth size="small" value={form.instrumentId}
                  onChange={set('instrumentId')} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Calibration Date" type="date" fullWidth size="small"
                  value={form.instrumentCalibrationDate} onChange={set('instrumentCalibrationDate')}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
              onChange={set('notes')} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Record' : 'Save Record'}
          </Button>
          <Button onClick={() => navigate('/milling/aflatoxin')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default AflatoxinForm;
